import { Navigate, useParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Building, LogOut } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { PiktableLoader } from "@/components/PiktableLoader";

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireSuperAdmin?: boolean;
  requireAdmin?: boolean;
}

export function ProtectedRoute({ children, requireSuperAdmin, requireAdmin }: ProtectedRouteProps) {
  const { session, loading, isSuperAdmin, isAdmin, establishmentId, signOut } = useAuth();
  const { slug } = useParams<{ slug?: string }>();
  const dashboardPath = slug ? `/${slug}/dashboard` : "/dashboard";
  const loginPath = slug ? `/${slug}/login` : "/login";

  if (loading) {
    return <PiktableLoader message="Connexion en cours..." />;
  }

  if (!session) {
    return <Navigate to={loginPath} replace />;
  }

  if (requireSuperAdmin && !isSuperAdmin) {
    return <Navigate to={dashboardPath} replace />;
  }

  if (requireAdmin && !isAdmin) {
    return <Navigate to={dashboardPath} replace />;
  }

  // Utilisateurs sans établissement (sauf super admins) voient un dialog bloquant
  if (!isSuperAdmin && !establishmentId) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30">
        <Dialog open={true}>
          <DialogContent className="sm:max-w-md" hideCloseButton>
            <DialogHeader>
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-amber-100">
                <Building className="h-6 w-6 text-amber-600" />
              </div>
              <DialogTitle className="text-center">
                Aucun établissement assigné
              </DialogTitle>
              <DialogDescription className="text-center">
                Votre compte n'est pas encore associé à un établissement. 
                Veuillez contacter votre administrateur pour être assigné à un établissement.
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-col gap-3 mt-4">
              <p className="text-sm text-muted-foreground text-center">
                Si vous êtes propriétaire d'un établissement, contactez le support Piktable.
              </p>
              <Button 
                variant="outline" 
                onClick={() => signOut()}
                className="w-full"
              >
                <LogOut className="mr-2 h-4 w-4" />
                Se déconnecter
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  return <>{children}</>;
}
